// src/core/common/Sidebar.tsx
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Scrollbars from "react-custom-scrollbars-2";
import ImageWithBasePath from "../imageWithBasePath";
import "../../../style/icon/tabler-icons/webfont/tabler-icons.css";
import { setExpandMenu } from "../../data/redux/sidebarSlice";
import { useDispatch } from "react-redux";
import {
  resetAllMode,
  setDataLayout,
} from "../../data/redux/themeSettingSlice";
import usePreviousRoute from "./usePreviousRoute";

// ** NOVO: modal de ajuda **
import HelpModal from "./HelpModal";

// ** Hook para pegar páginas dinâmicas (Funil) e montar submenu **
import { useSidebarData as useHorizontalSidebarData } from "../../data/json/horizontalSidebar";

const VerticalSidebar = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const prev = usePreviousRoute();
  const rawSidebarData = useHorizontalSidebarData(); 

  // 🔹 Filtra para manter os itens desejados no menu vertical mobile
  const sidebarData = rawSidebarData.filter(item =>
    ["Chat", "Biblioteca", "Respostas Rápidas", "Funil", "Funis (Admin)", "Agendamentos", "Administração", "Gerenciamento de Equipe", "Monitoramento"].includes(item.menuValue)
  );

  // controla dropdown interno (primeiro nível)
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  const toggleMenu = (label: string) => {
    setOpenMenus(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  const [userEmail, setUserEmail] = useState<string | null>(null);
  const foneIns = localStorage.getItem("fone_ins");
  const isConnected = localStorage.getItem("isConnected") === "true";
  const [showHelpModal, setShowHelpModal] = useState(false);

  useEffect(() => {
    setUserEmail(localStorage.getItem("user_email"));
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/entrar";
  };

  const handlePrecisaAjuda = () => {
    setShowHelpModal(true);
  };

  const handleSubMenuClick = (sub: any) => {
    // Se o item tiver parâmetros (menu admin de funis), salva no localStorage
    if (sub.params) {
        if (sub.params.funnelName) localStorage.setItem("nome_pai", sub.params.funnelName);
        if (sub.params.teamId) localStorage.setItem("id_cliente_filter", sub.params.teamId);
        if (sub.params.userId) localStorage.setItem("id_user_filter", sub.params.userId);
    } else {
        // Comportamento padrão (usuário comum)
        localStorage.setItem("nome_pai", sub.menuValue);
        localStorage.removeItem("id_cliente_filter");
        localStorage.removeItem("id_user_filter");
    }

    // Navegação/Reload
    if (sub.route && sub.route !== "#") {
      window.location.href = sub.route;
    } else {
      // Se não tem rota, talvez seja apenas um agrupador (Time ou Usuário no menu Admin)
      toggleMenu(sub.menuValue);
    }
  };

  const renderSubMenus = (menus: any[], level = 1) => {
    return (
      <ul style={{ display: "block", listStyle: "none", paddingLeft: level * 15 }}>
        {menus.map((sub, j) => {
          const hasChildren = sub.children && sub.children.length > 0;
          const isOpen = !!openMenus[sub.menuValue];

          return (
            <li key={j} className={hasChildren ? "submenu" : ""}>
              <a
                href="#"
                onClick={e => {
                  e.preventDefault();
                  handleSubMenuClick(sub);
                }}
                className={`px-3 d-flex align-items-center justify-content-between ${isOpen ? "subdrop" : ""}`}
                style={{ fontSize: "0.9rem", padding: "8px 0" }}
              >
                <span>{sub.menuValue}</span>
                {hasChildren && <i className={`ti ti-chevron-${isOpen ? "down" : "right"} fs-12`} />}
              </a>
              {hasChildren && isOpen && renderSubMenus(sub.children, level + 1)}
            </li>
          );
        })}
      </ul>
    );
  };

  const onMouseEnter = () => dispatch(setExpandMenu(true));
  const onMouseLeave = () => dispatch(setExpandMenu(false));

  return (
    <>
      <div
        className="sidebar"
        id="sidebar"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div className="sidebar-logo">
          <Link to="#" className="logo logo-normal">
            <ImageWithBasePath src="assets/img/logogoo.png" alt="Logo" />
          </Link>
        </div>

        {/* modern-profile pode exibir email e conexão */}
        <div className="modern-profile p-3 pb-0">
          <div className="text-center rounded bg-light p-3 mb-4 user-profile">
            <div className="avatar avatar-lg online mb-3">
              <ImageWithBasePath
                src="assets/img/profiles/avatar-02.jpg"
                alt="Img"
                className="img-fluid rounded-circle"
              />
            </div>
            {userEmail && (
              <h6 className="fs-12 fw-normal mb-1">{userEmail}</h6>
            )}
            {foneIns && (
              <p className="fs-10">
                {isConnected
                  ? <span>🟢 {foneIns}</span>
                  : <span style={{color:"#b8b8b8"}}>Aguardando conexão...</span>
                }
              </p>
            )}
          </div>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <button
              onClick={handlePrecisaAjuda}
              className="btn btn-sm btn-link"
              style={{ textDecoration: "underline" }}
            >
              Precisa de ajuda?
            </button>
            <button
              onClick={handleLogout}
              className="btn btn-sm btn-link text-danger d-flex align-items-center"
            >
              <i className="ti ti-log-out me-1" /> Sair
            </button>
          </div>
        </div>

        <Scrollbars>
          <div className="sidebar-inner slimscroll">
            <div id="sidebar-menu" className="sidebar-menu">
              <ul>
                {sidebarData.map((item, idx) => {
                  const hasSub = (item.subMenus && item.subMenus.length > 0);
                  const isOpen = !!openMenus[item.menuValue];

                  return (
                    <li key={idx} className={`menu-item ${hasSub ? "submenu" : ""}`}>
                      <Link
                        to={item.route || "#"}
                        onClick={e => {
                          if (hasSub) {
                            e.preventDefault();
                            toggleMenu(item.menuValue);
                          } else if (item.route) {
                            window.location.href = item.route;
                          }
                        }}
                        className={ isOpen ? "subdrop" : "" }
                      >
                        <i className={`ti ti-${item.icon}`} />
                        <span>{item.menuValue}</span>
                        {hasSub && <span className="menu-arrow" />}
                      </Link>

                      {hasSub && isOpen && renderSubMenus(item.subMenus || [])}
                    </li>
                  );
                })}
                {/* Item de Logout explícito no menu */}
                <li className="menu-item">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleLogout();
                    }}
                    className="text-danger"
                  >
                    <i className="ti ti-log-out" />
                    <span>Sair</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </Scrollbars>
      </div>

      {/* Modal de ajuda vindo do HorizontalSidebar */}
      <HelpModal show={showHelpModal} onHide={() => setShowHelpModal(false)} />
    </>
  );
};

export default VerticalSidebar;
